import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "../../components/common/Button";
import DataTable from "../../components/common/DataTable";
import reportService from "../../services/reportService";

function formatNumber(value) {
  const number = Number(value || 0);
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(number);
}

function formatWeight(value) {
  return `${formatNumber(value)} kg`;
}

function formatCurrency(value) {
  return `₹ ${formatNumber(value)}`;
}

function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function BillReportPage() {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadingFormat, setDownloadingFormat] = useState("");
  const [error, setError] = useState("");

  const loadReport = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const report = await reportService.getBillReport();
      setRows(report.items);
    } catch (err) {
      setRows([]);
      setError(err?.message || "Failed to load Bill report.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, item) => ({
          totalAmount: acc.totalAmount + toNumber(item.totalAmount),
          totalWeight: acc.totalWeight + toNumber(item.totalWeight),
          totalPriceinRS: acc.totalPriceinRS + toNumber(item.totalPriceinRS),
          totalExpenses: acc.totalExpenses + toNumber(item.totalExpenses),
          totalPriceInRSWithExpenses: acc.totalPriceInRSWithExpenses + toNumber(item.totalPriceInRSWithExpenses),
        }),
        { 
          totalAmount: 0, 
          totalWeight: 0, 
          totalPriceinRS: 0, 
          totalExpenses: 0, 
          totalPriceInRSWithExpenses: 0 
        }
      ),
    [rows]
  );

  const columns = useMemo(
    () => [
      {
        key: "billno",
        label: "Bill No",
        sortable: true,
        render: (item) => (
          <span className="font-bold text-slate-900 dark:text-white">{item.billno || "-"}</span>
        ),
      },
      { key: "bookingNo", label: "Booking No", sortable: true },
      { key: "blNumber", label: "BL Number", sortable: true },
      { key: "vendorName", label: "Vendor", sortable: true },
      {
        key: "billDate",
        label: "Bill Date",
        sortable: true,
        render: (item) => item.billDate ? new Date(item.billDate).toLocaleDateString() : "-",
      },
      {
        key: "totalWeight",
        label: "Weight",
        sortable: true,
        headerClassName: "text-right",
        render: (item) => <span className="block w-full text-right">{formatWeight(item.totalWeight)}</span>,
      },
      {
        key: "totalAmount",
        label: "Amount",
        sortable: true,
        headerClassName: "text-right",
        render: (item) => <span className="block w-full text-right">{formatNumber(item.totalAmount)}</span>,
      },
      {
        key: "totalPriceinRS",
        label: "Price (RS)",
        sortable: true,
        headerClassName: "text-right",
        render: (item) => <span className="block w-full text-right">{formatCurrency(item.totalPriceinRS)}</span>,
      },
      {
        key: "totalExpenses",
        label: "Expenses",
        sortable: true,
        headerClassName: "text-right",
        render: (item) => <span className="block w-full text-right">{formatCurrency(item.totalExpenses)}</span>,
      },
      {
        key: "totalPriceInRSWithExpenses",
        label: "Total with Exp.",
        sortable: true,
        headerClassName: "text-right",
        render: (item) => (
          <span className="block w-full text-right font-bold text-primary-700 dark:text-primary-300">
            {formatCurrency(item.totalPriceInRSWithExpenses)}
          </span>
        ),
      },
      { key: "memo", label: "Memo", sortable: true },
    ],
    []
  );

  const handleRefresh = () => {
    loadReport();
  };

  const handleDownload = async (format) => {
    setDownloadingFormat(format);
    setError("");
    try {
      const blob = await reportService.downloadBillReport(format);
      const extension = format === "xlsx" ? "xlsx" : format;
      downloadBlob(blob, `bill-report.${extension}`);
    } catch (err) {
      setError(err?.message || `Failed to download ${format.toUpperCase()} report.`);
    } finally {
      setDownloadingFormat("");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 dark:border-slate-800 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Reports
          </p>
          <h1 className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
            Bill Report
          </h1>
        </div>

        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:items-center">
          <Button 
            variant="secondary" 
            onClick={handleRefresh} 
            isLoading={isLoading}
            className="w-full sm:w-auto"
          >
            <span className="flex items-center justify-center gap-1.5 text-[10px] sm:text-xs">
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v6h6M20 20v-6h-6" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 9a8 8 0 00-14.9-3M4 15a8 8 0 0014.9 3" />
              </svg>
              Refresh
            </span>
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleDownload("xlsx")}
            isLoading={downloadingFormat === "xlsx"}
            disabled={Boolean(downloadingFormat)}
            className="w-full sm:w-auto"
          >
            <span className="flex items-center justify-center gap-1.5 text-[10px] sm:text-xs text-green-600 dark:text-green-400">
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 2H7a2 2 0 00-2 2v16a2 2 0 002 2h10a2 2 0 002-2V8z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 2v6h6M8 13h8M8 17h8" />
              </svg>
              EXCEL
            </span>
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleDownload("csv")}
            isLoading={downloadingFormat === "csv"}
            disabled={Boolean(downloadingFormat)}
            className="w-full sm:w-auto"
          >
            <span className="flex items-center justify-center gap-1.5 text-[10px] sm:text-xs">
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 2H7a2 2 0 00-2 2v16a2 2 0 002 2h10a2 2 0 002-2V8z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 2v6h6M8 13h8M8 17h8" />
              </svg>
              CSV
            </span>
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleDownload("pdf")}
            isLoading={downloadingFormat === "pdf"}
            disabled={Boolean(downloadingFormat)}
            className="w-full sm:w-auto"
          >
            <span className="flex items-center justify-center gap-1.5 text-[10px] sm:text-xs text-red-600 dark:text-red-400">
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 2H7a2 2 0 00-2 2v16a2 2 0 002 2h10a2 2 0 002-2V8z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 2v6h6M8 13h8M8 17h5" />
              </svg>
              PDF
            </span>
          </Button>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm font-medium text-danger">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-4">
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 sm:text-[11px]">
            Total Weight
          </p>
          <p className="mt-1 text-base font-black text-slate-950 dark:text-white sm:mt-2 sm:text-xl">
            {formatWeight(totals.totalWeight)}
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-4">
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 sm:text-[11px]">
            Total Amount
          </p>
          <p className="mt-1 text-base font-black text-slate-950 dark:text-white sm:mt-2 sm:text-xl">
            {formatNumber(totals.totalAmount)}
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-4">
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 sm:text-[11px]">
            Total Price (RS)
          </p>
          <p className="mt-1 text-base font-black text-slate-950 dark:text-white sm:mt-2 sm:text-xl">
            {formatCurrency(totals.totalPriceinRS)}
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-4">
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 sm:text-[11px]">
            Total Expenses
          </p>
          <p className="mt-1 text-base font-black text-slate-950 dark:text-white sm:mt-2 sm:text-xl">
            {formatCurrency(totals.totalExpenses)}
          </p>
        </div>
        <div className="col-span-2 rounded-xl border border-primary-200 bg-primary-50 p-3 shadow-sm dark:border-primary-900/50 dark:bg-primary-900/10 sm:p-4 md:col-span-1">
          <p className="text-[9px] font-bold uppercase tracking-wider text-primary-700 dark:text-primary-300 sm:text-[11px]">
            Grand Total (with Exp)
          </p>
          <p className="mt-1 text-base font-black text-primary-800 dark:text-primary-200 sm:mt-2 sm:text-xl">
            {formatCurrency(totals.totalPriceInRSWithExpenses)}
          </p>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        searchPlaceholder="Search bill report..."
        pageSize={10}
        scrollHeight="58vh"
      />
    </div>
  );
}

export default BillReportPage;
